import { getSupabaseClient } from './supabase';
import { apiFetch } from '../api/http';
import { toAppError } from '../utils/appErrors';

type MaybeArray<T> = T | T[] | null | undefined;

function asSingle<T>(value: MaybeArray<T>): T | null {
  if (Array.isArray(value)) return (value[0] as T | undefined) ?? null;
  return (value as T | null) ?? null;
}

function isMissingColumnError(error: { code?: string; message?: string } | null | undefined): boolean {
  if (!error) return false;
  return error.code === '42703' || /column .* does not exist/i.test(error.message || '');
}

export type PurchaseOrderLineRow = {
  po_line_id: string;
  po_id: string;
  description: string | null;
  item_id: string | null;
  qty: number | null;
  unit_cost: number | null;
  received_qty: number | null;
  status: string | null;
  disposition?: 'inventory' | 'event_sale' | 'cross_rental' | string | null;
  event_id?: string | null;
  event_line_id?: string | null;
  rental_start_at?: string | null;
  rental_end_at?: string | null;
  subrental_id?: string | null;
  temp_fleet_vehicle_id?: string | null;
  sold_qty?: number | null;
  item_is_bulk?: boolean | null;
  item_behavior_role?: string | null;
  items?: {
    item_code: string | null;
    name_internal: string | null;
    is_bulk?: boolean | null;
    item_types?: { behavior_role?: string | null } | Array<{ behavior_role?: string | null }> | null;
  } | Array<{
    item_code: string | null;
    name_internal: string | null;
    is_bulk?: boolean | null;
    item_types?: { behavior_role?: string | null } | Array<{ behavior_role?: string | null }> | null;
  }> | null;
};

export type PurchaseOrderReceiptSuggestionLine = {
  id: string;
  source_line_text: string | null;
  description: string | null;
  item_code: string | null;
  barcode: string | null;
  serial_number: string | null;
  suggested_po_line_id: string | null;
  suggested_received_qty: number;
  confidence: number | null;
  notes: string | null;
};

export type PurchaseOrderReceiptSuggestion = {
  receipt_id?: string | null;
  document_type: string | null;
  document_number: string | null;
  vendor_name: string | null;
  carrier: string | null;
  tracking_number: string | null;
  received_date: string | null;
  confidence: number | null;
  notes: string | null;
  lines: PurchaseOrderReceiptSuggestionLine[];
  pages_processed?: number;
  pages_total?: number;
  file_name?: string | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
};

export type ReviewedPurchaseOrderReceiptLine = {
  poLineId: string;
  receivedDelta: number;
  include?: boolean;
  sourceLineId?: string | null;
  sourceLineText?: string | null;
  description?: string | null;
  itemCode?: string | null;
  barcode?: string | null;
  serialNumber?: string | null;
  suggestedPoLineId?: string | null;
  suggestedReceivedQty?: number | null;
  confidence?: number | null;
  notes?: string | null;
  reviewNotes?: string | null;
};

export type PurchaseOrderReceiptAuditLine = {
  po_receipt_line_id: string;
  po_receipt_id: string;
  po_line_id: string | null;
  source_line_id: string | null;
  source_line_text: string | null;
  description: string | null;
  item_code: string | null;
  barcode: string | null;
  serial_number: string | null;
  suggested_po_line_id: string | null;
  suggested_received_qty: number | null;
  reviewed_received_qty: number | null;
  applied_received_qty: number | null;
  include: boolean;
  confidence: number | null;
  notes: string | null;
  review_notes: string | null;
  decision: string;
  created_at: string;
  updated_at: string;
};

export type PurchaseOrderReceiptAuditRow = {
  po_receipt_id: string;
  po_id: string;
  source_kind: string;
  status: string;
  document_type: string | null;
  document_number: string | null;
  vendor_name: string | null;
  carrier: string | null;
  tracking_number: string | null;
  received_date: string | null;
  confidence: number | null;
  notes: string | null;
  storage_bucket: string | null;
  storage_path: string | null;
  original_file_name: string | null;
  content_type: string | null;
  file_size_bytes: number | null;
  pages_processed: number | null;
  pages_total: number | null;
  ai_model: string | null;
  created_by: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  applied_by: string | null;
  applied_at: string | null;
  created_at: string;
  updated_at: string;
  purchase_order_receipt_lines?: PurchaseOrderReceiptAuditLine[] | null;
};

export type VendorRentalProductOption = {
  product_id: string;
  product_code: string | null;
  name_public: string | null;
  category_id: string | null;
  r2_subcategoryiid: string | null;
  link_rank: number | null;
  is_default: boolean;
};

export type VendorRentalRoiSummary = {
  subrental_id: string;
  po_line_id: string | null;
  item_id: string | null;
  product_id: string | null;
  product_code: string | null;
  product_name: string | null;
  r2_subcategoryiid: string | null;
  rental_term: string;
  rental_days: number;
  rental_quantity: number;
  event_count: number;
  usage_count: number;
  attributed_quantity: number;
  usage_unit_days: number;
  attributed_revenue: number;
  contracted_vendor_cost: number | null;
  gross_margin: number | null;
  roi_pct: number | null;
  utilization_pct: number | null;
};

export type VendorDirectoryRow = {
  source_kind: 'vendor' | 'supplier';
  source_id: string;
  vendor_id: string | null;
  supplier_id: string | null;
  name: string;
  contact_json: Record<string, unknown> | null;
  active: boolean;
};

export type LocationOption = {
  location_id: string;
  name: string;
};

export type VendorOption = {
  vendor_id: string;
  name: string;
  active: boolean;
};

export type SupplierOption = {
  supplier_id: string;
  name: string;
  active: boolean;
};

export async function createPurchaseOrderReceiptFileUrl(
  receipt: Pick<PurchaseOrderReceiptAuditRow, 'storage_bucket' | 'storage_path'>,
): Promise<string | null> {
  if (!receipt.storage_path) return null;
  const supabase = getSupabaseClient();
  const bucket = receipt.storage_bucket || 'purchase-order-receipts';
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(receipt.storage_path, 60 * 10);
  if (error) throw error;
  return data?.signedUrl ?? null;
}

export async function fetchVendorRentalProductOptions(itemId: string): Promise<VendorRentalProductOption[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await (supabase.rpc as any)('vendor_rental_product_options', {
    p_item_id: itemId,
  });
  if (error) throw error;
  return (Array.isArray(data) ? data : []) as VendorRentalProductOption[];
}

export async function recordVendorRentalReceipt(params: {
  poLineId: string;
  receivedDelta: number;
  externalIdentifiers?: string[];
}): Promise<{ applied_qty: number; received_qty: number; identifier_count: number }> {
  const supabase = getSupabaseClient();
  const identifiers = Array.from(new Set(
    (params.externalIdentifiers || []).map((value) => value.trim()).filter(Boolean),
  ));
  const { data, error } = await (supabase.rpc as any)('record_vendor_rental_receipt', {
    p_po_line_id: params.poLineId,
    p_received_delta: Math.max(0, Number(params.receivedDelta) || 0),
    p_external_identifiers: identifiers.length ? identifiers : null,
    p_update_po_line: true,
  });
  if (error) throw error;
  const payload = (data || {}) as Record<string, unknown>;
  if (payload.success === false) {
    throw new Error(String(payload.error || 'Vendor rental receipt was not applied'));
  }
  return {
    applied_qty: Number(payload.applied_qty ?? 0),
    received_qty: Number(payload.received_qty ?? 0),
    identifier_count: Number(payload.identifier_count ?? 0),
  };
}

export async function fetchVendorRentalRoiSummary(subrentalId: string): Promise<VendorRentalRoiSummary> {
  const supabase = getSupabaseClient();
  const { data, error } = await (supabase.rpc as any)('vendor_rental_roi_summary', {
    p_subrental_id: subrentalId,
  });
  if (error) throw error;
  if (!data || typeof data !== 'object') throw new Error('Vendor rental ROI summary was empty');
  return data as VendorRentalRoiSummary;
}

export async function fetchFulfillmentVendorDirectory(limit = 500): Promise<VendorDirectoryRow[]> {
  const supabase = getSupabaseClient();
  const [vendorResult, supplierResult] = await Promise.all([
    supabase
      .from('vendors')
      .select('vendor_id,name,contact_json,active')
      .order('name', { ascending: true })
      .limit(limit),
    supabase
      .from('suppliers')
      .select('supplier_id,name,contact_json,active')
      .order('name', { ascending: true })
      .limit(limit),
  ]);
  if (vendorResult.error) throw vendorResult.error;
  if (supplierResult.error) throw supplierResult.error;
  return [
    ...((vendorResult.data || []) as Array<{ vendor_id: string; name: string; contact_json: Record<string, unknown> | null; active: boolean }>).map((row) => ({
      source_kind: 'vendor' as const,
      source_id: String(row.vendor_id),
      vendor_id: row.vendor_id,
      supplier_id: null,
      name: row.name,
      contact_json: row.contact_json ?? null,
      active: row.active,
    })),
    ...((supplierResult.data || []) as Array<{ supplier_id: string; name: string; contact_json: Record<string, unknown> | null; active: boolean }>).map((row) => ({
      source_kind: 'supplier' as const,
      source_id: String(row.supplier_id),
      vendor_id: null,
      supplier_id: row.supplier_id,
      name: row.name,
      contact_json: row.contact_json ?? null,
      active: row.active,
    })),
  ]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    .slice(0, limit);
}

export async function fetchLocationOptions(): Promise<LocationOption[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('locations')
    .select('location_id,name')
    .eq('active', true)
    .order('name', { ascending: true });
  if (error) throw error;
  return (data || []) as LocationOption[];
}

export async function fetchVendorOptions(): Promise<VendorOption[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('vendors')
    .select('vendor_id,name,active')
    .order('name', { ascending: true });
  if (error) throw error;
  return (data || []) as VendorOption[];
}

export async function fetchSupplierOptions(): Promise<SupplierOption[]> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('suppliers')
    .select('supplier_id,name,active')
    .order('name', { ascending: true });
  if (error) throw error;
  return (data || []) as SupplierOption[];
}

export async function updatePurchaseOrder(
  poId: string,
  patch: {
    status?: string;
    notes?: string | null;
    expected_delivery_at?: string | null;
    vendor_id?: string | null;
  },
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from('purchase_orders')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('po_id', poId);
  if (error) throw error;
}

export async function extendTempFleetVehicleRental(params: {
  vehicleId: string;
  newEndAt: string;
  reason?: string | null;
}): Promise<void> {
  const supabase = getSupabaseClient();
  const { data, error } = await (supabase.rpc as any)('extend_temp_fleet_vehicle_rental', {
    p_vehicle_id: params.vehicleId,
    p_new_end_at: params.newEndAt,
    p_reason: params.reason ?? null,
  });
  if (error) throw error;
  const result = data as any;
  if (result && result.success === false) {
    throw new Error(result.error || 'Unable to extend rental');
  }
}

export async function createPurchaseOrderLines(
  poId: string,
  lines: Array<{
    description: string;
    item_id?: string | null;
    qty: number;
    unit_cost?: number | null;
    received_qty?: number | null;
    status?: string;
    disposition?: string | null;
    event_id?: string | null;
    event_line_id?: string | null;
    rental_start_at?: string | null;
    rental_end_at?: string | null;
  }>,
): Promise<void> {
  if (!lines.length) return;
  const supabase = getSupabaseClient();
  const payload = lines.map((line) => ({
    po_id: poId,
    description: line.description.trim() || 'PO line',
    item_id: line.item_id ?? null,
    qty: Math.max(1, Number(line.qty) || 1),
    unit_cost: line.unit_cost ?? null,
    received_qty: Math.max(0, Number(line.received_qty) || 0),
    status: line.status || 'pending',
    disposition: line.disposition || 'inventory',
    event_id: line.event_id ?? null,
    event_line_id: line.event_line_id ?? null,
    rental_start_at: line.rental_start_at ?? null,
    rental_end_at: line.rental_end_at ?? null,
  }));
  const { error } = await supabase.from('purchase_order_lines').insert(payload);
  if (error) throw error;
}

export async function updatePurchaseOrderLine(
  poLineId: string,
  patch: {
    description?: string;
    qty?: number;
    unit_cost?: number | null;
    received_qty?: number | null;
    status?: string;
    disposition?: string | null;
    event_id?: string | null;
    event_line_id?: string | null;
    rental_start_at?: string | null;
    rental_end_at?: string | null;
  },
): Promise<void> {
  const supabase = getSupabaseClient();
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  if (patch.description != null) payload.description = patch.description;
  if (patch.qty != null) payload.qty = Math.max(1, Number(patch.qty) || 1);
  if (patch.unit_cost !== undefined) payload.unit_cost = patch.unit_cost;
  if (patch.received_qty !== undefined) payload.received_qty = Math.max(0, Number(patch.received_qty) || 0);
  if (patch.status != null) payload.status = patch.status;
  if (patch.disposition != null) payload.disposition = patch.disposition;
  if (patch.event_id !== undefined) payload.event_id = patch.event_id;
  if (patch.event_line_id !== undefined) payload.event_line_id = patch.event_line_id;
  if (patch.rental_start_at !== undefined) payload.rental_start_at = patch.rental_start_at;
  if (patch.rental_end_at !== undefined) payload.rental_end_at = patch.rental_end_at;
  const { error } = await supabase.from('purchase_order_lines').update(payload).eq('po_line_id', poLineId);
  if (error) throw error;
}

/**
 * Applies a reviewed packing-slip receipt in one transaction (apply_purchase_order_receipt keeps the
 * slip on its order and refuses a second apply). Receiving by hand goes through
 * purchase_order_receive (data/purchaseOrderRecord.ts).
 */
export async function receivePurchaseOrderLines(
  lines: ReviewedPurchaseOrderReceiptLine[],
  options: { receiptId: string | null | undefined },
): Promise<void> {
  const hasReceivedLine = lines.some(
    (line) => line.include !== false && line.poLineId && Math.max(0, Number(line.receivedDelta) || 0) > 0,
  );
  if (!hasReceivedLine) throw new Error('Select at least one reviewed receipt line to apply');
  if (!options.receiptId) throw new Error('This packing slip was not saved. Scan it again before applying.');

  const reviewedLines = lines.map((line) => ({
    po_line_id: line.poLineId || null,
    received_delta: Math.max(0, Number(line.receivedDelta) || 0),
    include: Boolean(line.include ?? (line.poLineId && Number(line.receivedDelta) > 0)),
    source_line_id: line.sourceLineId ?? null,
    source_line_text: line.sourceLineText ?? null,
    description: line.description ?? null,
    item_code: line.itemCode ?? null,
    barcode: line.barcode ?? null,
    serial_number: line.serialNumber ?? null,
    suggested_po_line_id: line.suggestedPoLineId ?? null,
    suggested_received_qty: Math.max(0, Number(line.suggestedReceivedQty) || 0),
    confidence: typeof line.confidence === 'number' ? line.confidence : null,
    notes: line.notes ?? null,
    review_notes: line.reviewNotes ?? null,
  }));
  const { error } = await getSupabaseClient().rpc('apply_purchase_order_receipt', {
    p_receipt_id: options.receiptId,
    p_reviewed_lines: reviewedLines,
  });
  if (error) throw error;
}

export async function parsePurchaseOrderReceiptDocument(params: {
  poId: string;
  file: File;
  lines: Array<PurchaseOrderLineRow & { item_name?: string | null; item_code?: string | null }>;
}): Promise<PurchaseOrderReceiptSuggestion> {
  const form = new FormData();
  form.append('file', params.file);
  form.append('po_id', params.poId);
  form.append(
    'lines',
    JSON.stringify(
      params.lines.map((line) => ({
        po_line_id: line.po_line_id,
        description: line.description,
        item_code: line.item_code,
        item_name: line.item_name,
        qty: line.qty,
        received_qty: line.received_qty,
      })),
    ),
  );

  const response = await apiFetch('/admin/ai/scan-purchase-receipt', {
    method: 'POST',
    body: form,
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw toAppError(
      { status: response.status },
      'The packing slip could not be scanned.',
      'purchase_receipt_scan',
      'mutation',
    );
  }
  const lines = Array.isArray(body?.lines) ? body.lines : [];
  return {
    receipt_id: body?.receipt_id ?? null,
    document_type: body?.document_type ?? null,
    document_number: body?.document_number ?? null,
    vendor_name: body?.vendor_name ?? null,
    carrier: body?.carrier ?? null,
    tracking_number: body?.tracking_number ?? null,
    received_date: body?.received_date ?? null,
    confidence: typeof body?.confidence === 'number' ? body.confidence : null,
    notes: body?.notes ?? null,
    pages_processed: body?.pages_processed,
    pages_total: body?.pages_total,
    file_name: body?.file_name ?? null,
    storage_bucket: body?.storage_bucket ?? null,
    storage_path: body?.storage_path ?? null,
    lines: lines.map((line: any, index: number) => ({
      id: String(line.id || `${index + 1}`),
      source_line_text: line.source_line_text ?? null,
      description: line.description ?? null,
      item_code: line.item_code ?? null,
      barcode: line.barcode ?? null,
      serial_number: line.serial_number ?? null,
      suggested_po_line_id: line.suggested_po_line_id ?? null,
      suggested_received_qty: Math.max(0, Number(line.suggested_received_qty ?? line.quantity ?? 0) || 0),
      confidence: typeof line.confidence === 'number' ? line.confidence : null,
      notes: line.notes ?? null,
    })),
  };
}

export async function returnXRental(subrentalId: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc('return_subrental', {
    p_subrental_id: subrentalId,
    p_delete_asset: true,
  });
  if (error) throw error;
  if (data && typeof data === 'object' && (data as Record<string, unknown>).success === false) {
    throw new Error(String((data as Record<string, unknown>).error || 'Vendor rental return was not applied'));
  }
}

export async function createVendor(params: {
  name: string;
  active?: boolean;
  contact_json?: Record<string, unknown> | null;
}): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from('vendors')
    .insert({
      name: params.name.trim(),
      active: params.active ?? true,
      contact_json: params.contact_json ?? {},
    });
  if (error) throw error;
}

export async function createSupplier(params: {
  name: string;
  active?: boolean;
  contact_json?: Record<string, unknown> | null;
}): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from('suppliers')
    .insert({
      name: params.name.trim(),
      active: params.active ?? true,
      contact_json: params.contact_json ?? {},
    });
  if (error) throw error;
}

export async function updateVendorDirectoryEntry(params: {
  source_kind: 'vendor' | 'supplier';
  source_id: string;
  name?: string;
  active?: boolean;
  contact_json?: Record<string, unknown> | null;
}): Promise<void> {
  const supabase = getSupabaseClient();
  const updatePayload: Record<string, unknown> = {};
  if (params.name != null) updatePayload.name = params.name.trim();
  if (params.active != null) updatePayload.active = params.active;
  if (params.contact_json != null) updatePayload.contact_json = params.contact_json;
  if (Object.keys(updatePayload).length === 0) return;

  if (params.source_kind === 'vendor') {
    const { error } = await supabase.from('vendors').update(updatePayload).eq('vendor_id', params.source_id);
    if (error) throw error;
    return;
  }
  const { error } = await supabase.from('suppliers').update(updatePayload).eq('supplier_id', params.source_id);
  if (error) throw error;
}
